import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { BillingApprovalView } from '@wodi/shared';
import { Button, EmptyState, LoadingBlock, Notice, PageHeader } from '@wodi/ui';
import { useState } from 'react';

import {
  approvalsQuery,
  billingKeys,
  decideAdjustment,
  decideCreditNote,
  decideRefund,
} from '../api/billing.ts';
import {
  CREDIT_REASON_LABELS,
  REFUND_METHOD_LABELS,
  REFUND_REASON_LABELS,
} from '../billing/credit-refund.tsx';
import styles from '../billing/billing.module.css';
import { money } from '../billing/format.ts';
import { WAIVER_REASON_LABELS } from '../billing/waiver-dialog.tsx';
import { useOnline } from '../hooks/use-online.ts';
import { formatInstant } from '../patients/format.ts';
import { ReasonDialog } from '../patients/reason-dialog.tsx';
import { errorText } from '../stock/common.tsx';
import { toasts } from '../toasts.ts';

/**
 * Billing approvals (plan §6.4): waivers and discounts waiting for someone other than the
 * person who asked. It works at phone width, so an owner can approve from a phone.
 */
export function BillingApprovalsPage() {
  const approvals = useQuery({ ...approvalsQuery, refetchInterval: 15_000 });
  const waiting = approvals.isSuccess
    ? approvals.data.items.length +
      approvals.data.creditNotes.length +
      approvals.data.refunds.length
    : 0;
  return (
    <div className={styles.page}>
      <PageHeader title="Approvals" />
      {waiting > 0 ? (
        <p className={styles.meta}>
          {waiting === 1 ? '1 request waiting.' : `${String(waiting)} requests waiting.`}
        </p>
      ) : null}
      {approvals.isPending ? <LoadingBlock label="Loading requests" rows={3} /> : null}
      {approvals.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void approvals.refetch()}>
              Try again
            </Button>
          }
        >
          {errorText(approvals.error, "Requests couldn't be loaded. Try again.")}
        </Notice>
      ) : null}
      {approvals.isSuccess && waiting === 0 ? (
        <EmptyState>Nothing is waiting for approval.</EmptyState>
      ) : null}
      <div className={styles.requests}>
        {approvals.isSuccess
          ? approvals.data.items.map((item) => <Request key={item.adjustment.id} item={item} />)
          : null}
        {approvals.isSuccess
          ? approvals.data.creditNotes.map((c) => (
              <Decision
                key={c.creditNote.id}
                title={`Credit note of ${money(c.creditNote.total)}`}
                label={`Credit note for ${c.patient.name}`}
                lines={[
                  `${c.patient.name}. Invoice ${c.creditNote.invoiceNumber}: ${c.creditNote.lines
                    .map((l) => `${l.description} ${money(l.amount)}`)
                    .join(', ')}.`,
                  `Asked by ${c.creditNote.requestedBy.name}, ${formatInstant(c.creditNote.requestedAt)}. Reason: ${
                    CREDIT_REASON_LABELS[
                      c.creditNote.reasonCode as keyof typeof CREDIT_REASON_LABELS
                    ]
                  }${c.creditNote.reasonNote === null ? '' : `. ${c.creditNote.reasonNote}`}`,
                ]}
                visitId={c.creditNote.visitId}
                visitNumber={c.visitNumber}
                canDecide={c.creditNote.canDecide}
                decide={(action, note) => decideCreditNote(c.creditNote.id, action, note)}
              />
            ))
          : null}
        {approvals.isSuccess
          ? approvals.data.refunds.map((r) => (
              <Decision
                key={r.refund.id}
                title={`Refund of ${money(r.refund.amount)}`}
                label={`Refund for ${r.patient.name}`}
                lines={[
                  `${r.patient.name}, by ${REFUND_METHOD_LABELS[r.refund.method].toLowerCase()}.`,
                  `Asked by ${r.refund.requestedBy.name}, ${formatInstant(r.refund.requestedAt)}. Reason: ${
                    REFUND_REASON_LABELS[r.refund.reasonCode as keyof typeof REFUND_REASON_LABELS]
                  }${r.refund.reasonNote === null ? '' : `. ${r.refund.reasonNote}`}`,
                ]}
                visitId={r.refund.visitId}
                visitNumber={r.visitNumber}
                canDecide={r.refund.canDecide}
                decide={(action, note) => decideRefund(r.refund.id, action, note)}
              />
            ))
          : null}
      </div>
    </div>
  );
}

function Request({ item }: { item: BillingApprovalView }) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const [rejecting, setRejecting] = useState(false);
  const a = item.adjustment;
  const decide = useMutation({
    mutationFn: ({ action, note }: { action: 'approve' | 'reject'; note?: string }) =>
      decideAdjustment(a.id, action, note),
    onSuccess: async (_result, { action }) => {
      await queryClient.invalidateQueries({ queryKey: billingKeys.all });
      setRejecting(false);
      toasts.show({ title: action === 'approve' ? 'Approved' : 'Rejected' });
    },
  });
  const what = a.kind === 'discount' ? 'Discount' : 'Waiver';
  const reason =
    a.reasonCode in WAIVER_REASON_LABELS
      ? WAIVER_REASON_LABELS[a.reasonCode as keyof typeof WAIVER_REASON_LABELS]
      : a.reasonCode;
  return (
    <section className={styles.request} aria-label={`${what} for ${item.patient.name}`}>
      <h2 className={styles.sectionHeading}>{`${what} of ${money(a.amount)}`}</h2>
      <p>
        {`${item.patient.name}, ${item.itemName}. The patient's share is ${money(item.patientShare)}.`}
      </p>
      <p className={styles.meta}>
        <span>Visit </span>
        <Link to="/cashier" search={{ visitId: a.visitId }}>
          <span className="wodi-id">{item.visitNumber}</span>
        </Link>
        <span>{`. Asked by ${a.requestedBy.name}, ${formatInstant(a.requestedAt)}.`}</span>
      </p>
      <p>{`Reason: ${reason}${a.reasonNote === null ? '' : `. ${a.reasonNote}`}`}</p>
      {a.canDecide ? (
        <div className={styles.toolbar}>
          <Button
            variant="primary"
            isDisabled={!online}
            isPending={decide.isPending && !rejecting}
            pendingLabel="Approving"
            onPress={() => {
              decide.mutate({ action: 'approve' });
            }}
          >
            Approve
          </Button>
          <Button
            isDisabled={!online}
            onPress={() => {
              setRejecting(true);
            }}
          >
            Reject
          </Button>
        </div>
      ) : (
        <p>You asked for this, so someone else must decide it.</p>
      )}
      {decide.isError && !rejecting ? (
        <Notice tone="error">{errorText(decide.error, 'Try again.')}</Notice>
      ) : null}
      <ReasonDialog
        title={`Reject this ${what.toLowerCase()}?`}
        isOpen={rejecting}
        onOpenChange={setRejecting}
        confirmLabel="Reject"
        pendingLabel="Rejecting"
        isPending={decide.isPending}
        error={decide.isError ? errorText(decide.error, 'Try again.') : null}
        onConfirm={(note) => {
          decide.mutate({ action: 'reject', note });
        }}
      >
        <p>The person who asked sees your reason.</p>
      </ReasonDialog>
    </section>
  );
}

/** A credit note or refund to decide: what, for whom, why, and Approve or Reject. */
function Decision({
  title,
  label,
  lines,
  visitId,
  visitNumber,
  canDecide,
  decide,
}: {
  title: string;
  label: string;
  lines: string[];
  visitId: string;
  visitNumber: string;
  canDecide: boolean;
  decide: (action: 'approve' | 'reject', note?: string) => Promise<unknown>;
}) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const [rejecting, setRejecting] = useState(false);
  const mutation = useMutation({
    mutationFn: ({ action, note }: { action: 'approve' | 'reject'; note?: string }) =>
      decide(action, note),
    onSuccess: async (_result, { action }) => {
      await queryClient.invalidateQueries({ queryKey: billingKeys.all });
      setRejecting(false);
      toasts.show({ title: action === 'approve' ? 'Approved' : 'Rejected' });
    },
  });
  return (
    <section className={styles.request} aria-label={label}>
      <h2 className={styles.sectionHeading}>{title}</h2>
      {lines.map((line) => (
        <p key={line}>{line}</p>
      ))}
      <p className={styles.meta}>
        <span>Visit </span>
        <Link to="/cashier" search={{ visitId }}>
          <span className="wodi-id">{visitNumber}</span>
        </Link>
      </p>
      {canDecide ? (
        <div className={styles.toolbar}>
          <Button
            variant="primary"
            isDisabled={!online}
            isPending={mutation.isPending && !rejecting}
            pendingLabel="Approving"
            onPress={() => {
              mutation.mutate({ action: 'approve' });
            }}
          >
            Approve
          </Button>
          <Button
            isDisabled={!online}
            onPress={() => {
              setRejecting(true);
            }}
          >
            Reject
          </Button>
        </div>
      ) : (
        <p>You asked for this, so someone else must decide it.</p>
      )}
      {mutation.isError && !rejecting ? (
        <Notice tone="error">{errorText(mutation.error, 'Try again.')}</Notice>
      ) : null}
      <ReasonDialog
        title={`Reject this ${title.split(' ')[0]?.toLowerCase() ?? 'request'}?`}
        isOpen={rejecting}
        onOpenChange={setRejecting}
        confirmLabel="Reject"
        pendingLabel="Rejecting"
        isPending={mutation.isPending}
        error={mutation.isError ? errorText(mutation.error, 'Try again.') : null}
        onConfirm={(note) => {
          mutation.mutate({ action: 'reject', note });
        }}
      >
        <p>The person who asked sees your reason.</p>
      </ReasonDialog>
    </section>
  );
}
